import { Add, Delete } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Card, CardActions, CardContent, CardHeader, Divider, IconButton, InputAdornment, Tooltip, Typography } from '@mui/material'
import { cloneDeep, omit } from 'lodash'
import { ConfirmDialog, NumberTextField } from 'modules/common'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExpressPricesDialog from './ExpressPricesDialog'

export default function ExpressPricesCard({ settings, loading, error, isSuccess, onConfirm }) {
    const { t } = useTranslation()
    const [expressPrice, setExpressPrice] = useState([])

    const onConfirmHandler = () => {
        let payload = {
            expressPrice: expressPrice.map(elem => ({
                count: Number(elem?.count),
                price: Number(elem?.price)
            }))
        }
        onConfirm(payload)
    }

    useEffect(() => {
        if (!!settings?.expressPrice) {
            setExpressPrice(settings?.expressPrice)
        }
    }, [settings])

    const onChange = (index, name, value) => {
        setExpressPrice((prevExpressPrice) => {
            let data = cloneDeep(prevExpressPrice)
            data[index][name] = value
            return data
        })
    }

    //============Delete express price handler============
    const [selectedExpressPriceIndex, setSelectedExpressPriceIndex] = useState(null)
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
    const onDelete = (index) => {
        setExpressPrice((prevExpressPrice) => {
            let data = cloneDeep(prevExpressPrice)
            data.splice(index, 1)
            return data
        })
    }

    const confimDeleteExpressPrice = (index) => {
        setOpenConfirmDialog(true)
        setSelectedExpressPriceIndex(index)
    }

    //===========Express price dialog==============
    const [expressPriceValue, setExpressPriceValue] = useState({ count: '', price: '' })
    const [openExpressPriceDialog, setOpenExpressPriceDialog] = useState(false)

    const onChangeExpressPriceValue = ({ target: { name, value } }) => {
        setExpressPriceValue((prevState) => (
            {
                ...prevState,
                [name]: value
            }
        ))
    }

    const onAddExpressPrice = () => {
        if (!expressPriceValue?.count && !expressPriceValue?.price) {
            return
        }
        setExpressPrice((prevExpressPrice) => ([{ _id: new Date().getTime(), ...expressPriceValue }, ...prevExpressPrice]))
        closeDialogHandler()
    }
    const closeDialogHandler = () => {
        setOpenExpressPriceDialog(false)
        setExpressPriceValue({ count: '', price: '' })
    }

    return (
        <>
            <ConfirmDialog
                open={openConfirmDialog}
                onClose={() => {
                    setOpenConfirmDialog(false)
                    setSelectedExpressPriceIndex(null)
                }}
                title={t("Warning")}
                content={t("Are you sure you want to delete it?")}
                confirmFn={() => {
                    onDelete(selectedExpressPriceIndex)
                    setOpenConfirmDialog(false)
                    setSelectedExpressPriceIndex(null)
                }}
            />
            <ExpressPricesDialog
                open={openExpressPriceDialog}
                expressPriceValue={expressPriceValue}
                handleChange={onChangeExpressPriceValue}
                onAdd={onAddExpressPrice}
                handleClose={closeDialogHandler}
            />
            <Card sx={{ mt: 20 }}>
                <CardHeader
                    title={t("Express prices")}
                    subheader={t("Any changes will not be saved unless confirmed.")}
                    subheaderTypographyProps={{
                        color: "primary",
                        variant: "subtitle2"
                    }}
                    action={
                        <Tooltip title={t("Add express price")}>
                            <IconButton onClick={() => setOpenExpressPriceDialog(true)}><Add sx={{ color: "primary.main" }} /></IconButton>
                        </Tooltip>
                    }
                />
                <Divider />
                {
                    (!expressPrice?.length && !loading) ?
                        <CardContent>
                            <Typography color="warning.main">
                                {t("Express prices empty")}
                            </Typography>
                        </CardContent>
                        :
                        <CardContent>
                            {
                                expressPrice.map((elem, index) => (
                                    <Box key={elem?._id} display="flex" justifyContent="space-between" alignItems="center" sx={{ my: 20 }}>
                                        <NumberTextField
                                            name="count"
                                            label={t("CountShoes", { count: !!elem?.count ? `(${elem?.count})` : "(--)" })}
                                            placeholder={t("Count")}
                                            value={elem?.count ?? ''}
                                            onChange={({ target: { name, value } }) => onChange(index, name, value)}
                                            size="small"
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end"> <img src="/images/smart-shoe.svg" width={25} height={25} alt='shoe' /></InputAdornment>,
                                            }}
                                        />
                                        <NumberTextField
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">{t("Riyal")}</InputAdornment>,
                                            }}
                                            name="price"
                                            label={t("Price")}
                                            value={elem?.price ?? ''}
                                            onChange={({ target: { name, value } }) => onChange(index, name, value)}
                                            size="small"
                                        />
                                        <IconButton onClick={() => confimDeleteExpressPrice(index)}>
                                            <Delete sx={{ color: 'red' }} />
                                        </IconButton>
                                    </Box>
                                ))
                            }
                        </CardContent>
                }
                <Divider />
                <CardActions sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse' }}>
                    <LoadingButton sx={{ textTransform: "none" }} variant="contained" loading={loading} onClick={onConfirmHandler}>{t("Confirm")}</LoadingButton>
                    {isSuccess && <Typography color="success.main">{t("Done successfully")}</Typography>}
                    {!!error && <Typography color="error">{t(error)}</Typography>}
                </CardActions>
            </Card>
        </>
    )
}
